import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import {
  isFixedPageType,
  type TrainingAnswer,
  type TrainingAnswers,
  type TrainingModuleSchema,
  type TrainingRetryMode,
} from '@hcc/types';
import { ModalShell } from '../../ui/ModalShell';
import { Button } from '../../ui/Button';
import { Pill } from '../../ui/Pill';
import { ProgressBar } from '../../ui/ProgressBar';
import { ContentSlide } from '../player/ContentSlide';
import { QuestionSlide } from '../player/QuestionSlide';
import { QuestionGroupSlide } from '../player/QuestionGroupSlide';
import { TextGroupSlide } from '../player/TextGroupSlide';
import { FollowUpSlide } from '../player/FollowUpSlide';
import { SlideNavigator } from '../player/SlideNavigator';
import {
  isAnswered,
  pageComplete,
  pageQuestionCount,
  playerQuestions,
} from '../player/answerValues';
import { gradePreview, gradePreviewQuestion, previewServe, toPreviewPages } from './previewView';

/**
 * "Preview as learner": the full player experience over the working draft,
 * entirely client-side — no attempt is created and nothing is published. It
 * runs the same components, grading and (for a question bank) per-attempt
 * draw the real player does, so the admin sees exactly what a learner gets.
 */
export function PreviewAsLearnerModal({
  open,
  onClose,
  schema,
  graded = true,
  passMark,
  retryMode,
  serveQuestionCount,
  shuffleQuestions,
}: {
  open: boolean;
  onClose: () => void;
  schema: TrainingModuleSchema;
  /** False = an unmarked assessment: no score, no pass mark. */
  graded?: boolean;
  passMark: number;
  retryMode: TrainingRetryMode;
  serveQuestionCount: number | null;
  shuffleQuestions: boolean;
}) {
  const [drawSeed, setDrawSeed] = useState(1);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<TrainingAnswers>({});
  const [correctIds, setCorrectIds] = useState<ReadonlySet<string>>(new Set());
  const [lastCheck, setLastCheck] = useState<'correct' | 'incorrect' | null>(null);
  const [result, setResult] = useState<ReturnType<typeof gradePreview> | null>(null);

  const serve = useMemo(
    () => previewServe({ serveQuestionCount, shuffleQuestions }, drawSeed),
    [serveQuestionCount, shuffleQuestions, drawSeed],
  );
  const pages = useMemo(() => toPreviewPages(schema, serve), [schema, serve]);

  const tryAgain = retryMode === 'try_again';
  const questions = playerQuestions(pages);
  const safeStep = Math.min(step, Math.max(0, pages.length - 1));
  const page = pages[safeStep];
  const currentSection = page?.section_id
    ? schema.sections?.find((s) => s.id === page.section_id)
    : undefined;
  // try_again grades one question at a time, so it only applies to a
  // standalone question page (publish-time validation bars it with groups).
  const singleQuestion = !!page && !isFixedPageType(page.type);
  const isLocked = !!page && tryAgain && singleQuestion && correctIds.has(page.id);
  const allDone = tryAgain
    ? questions.every((q) => correctIds.has(q.id))
    : pages.every((p) => pageComplete(p, answers, pages));
  const nextBlocked = !!page && tryAgain && singleQuestion && !correctIds.has(page.id);

  function close() {
    // Reset so the next open starts a clean attempt with a fresh draw.
    setStep(0);
    setAnswers({});
    setCorrectIds(new Set());
    setLastCheck(null);
    setResult(null);
    setDrawSeed((s) => s + 1);
    onClose();
  }

  function restart() {
    setStep(0);
    setAnswers({});
    setCorrectIds(new Set());
    setLastCheck(null);
    setResult(null);
    setDrawSeed((s) => s + 1);
  }

  function setAnswer(next: TrainingAnswer) {
    if (!page) return;
    setLastCheck(null);
    setAnswers((a) => ({ ...a, [page.id]: next }));
  }

  function checkCurrent() {
    if (!page) return;
    const ok = gradePreviewQuestion(schema, page.id, answers[page.id]);
    setLastCheck(ok ? 'correct' : 'incorrect');
    if (ok) setCorrectIds((s) => new Set([...s, page.id]));
  }

  return (
    <ModalShell open={open} onClose={close} titleId="preview-as-learner" maxWidth="max-w-3xl">
      <div className="max-h-[80vh] overflow-y-auto p-1">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 id="preview-as-learner" className="text-base font-semibold text-fg">
            Preview as learner
          </h2>
          <Pill tone="accent" size="sm">
            Nothing is saved
          </Pill>
        </div>

        {result ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            {graded ? (
              <>
                <span
                  className={`flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold tabular-nums ${
                    result.passed
                      ? 'bg-success-soft/15 text-success-soft-fg'
                      : 'bg-warning-soft/15 text-warning-soft-fg'
                  }`}
                >
                  {Math.round(result.score)}%
                </span>
                <Pill tone={result.passed ? 'success' : 'warning'}>
                  {result.passed ? 'Would pass' : `Would fail (pass mark ${passMark}%)`}
                </Pill>
              </>
            ) : (
              <>
                <Pill tone="success">Answers recorded</Pill>
                <p className="max-w-sm text-sm text-fg-muted">
                  Nothing is marked, so the learner sees no score. Their answers are what gets
                  recorded and emailed.
                </p>
              </>
            )}
            {result.sections.length > 0 ? (
              <ul className="w-full max-w-sm space-y-1 text-left text-sm">
                {result.sections.map((s) => (
                  <li
                    key={s.section_id}
                    className="flex items-center justify-between gap-2 rounded-token-sm bg-surface-2 px-3 py-2"
                  >
                    <span className="truncate text-fg-muted">{s.title}</span>
                    {s.not_applicable ? (
                      <Pill tone="neutral" size="sm">
                        Not asked
                      </Pill>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="tabular-nums text-fg-dim">{Math.round(s.score)}%</span>
                        <Pill tone={s.passed ? 'success' : 'warning'} size="sm">
                          {s.passed ? 'Pass' : 'Fail'}
                        </Pill>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            <div className="flex gap-2">
              <Button
                variant="secondary"
                iconLeft={<RotateCcw className="h-4 w-4" />}
                onClick={restart}
              >
                Restart
              </Button>
              <Button variant="ghost" onClick={close}>
                Close
              </Button>
            </div>
          </div>
        ) : !page ? (
          <p className="py-8 text-center text-sm text-fg-muted">
            Add a page to preview the module.
          </p>
        ) : (
          <>
            <div className="mb-1.5 flex items-center justify-between text-xs text-fg-muted">
              <span>
                Page {safeStep + 1} of {pages.length}
              </span>
              <span className="font-semibold">
                {Math.round(((safeStep + 1) / pages.length) * 100)}%
              </span>
            </div>
            <ProgressBar value={safeStep + 1} total={pages.length} ariaLabel="Module progress" />

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <SlideNavigator
                pages={pages}
                step={safeStep}
                answers={answers}
                correctIds={correctIds}
                onJump={(i) => {
                  setLastCheck(null);
                  setStep(i);
                }}
              />
              {pageQuestionCount(page) > 0 ? (
                <span className="text-xs font-medium text-fg-muted">
                  {pageQuestionCount(page)} question{pageQuestionCount(page) === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>

            {currentSection?.title ? (
              <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-accent">
                {currentSection.title}
              </p>
            ) : null}

            <div className="mt-2 min-h-56">
              {page.type === 'slide' ? (
                <ContentSlide slide={page} />
              ) : page.type === 'question_group' ? (
                <QuestionGroupSlide
                  group={page}
                  answers={answers}
                  onChange={(rowId, optionId) => setAnswers((a) => ({ ...a, [rowId]: optionId }))}
                />
              ) : page.type === 'text_group' ? (
                <TextGroupSlide
                  group={page}
                  answers={answers}
                  onChange={(rowId, text) => setAnswers((a) => ({ ...a, [rowId]: text }))}
                />
              ) : page.type === 'follow_up' ? (
                <FollowUpSlide page={page} pages={pages} answers={answers} onChange={setAnswer} />
              ) : (
                <QuestionSlide
                  question={page}
                  answer={answers[page.id]}
                  onChange={setAnswer}
                  disabled={isLocked}
                />
              )}
            </div>

            {tryAgain && singleQuestion ? (
              <div className="min-h-5" aria-live="polite">
                {isLocked ? (
                  <p className="text-sm font-medium text-success-soft-fg">Correct — carry on.</p>
                ) : lastCheck === 'incorrect' ? (
                  <p className="text-sm font-medium text-warning-soft-fg">
                    Not quite — change your answer and try again.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 flex items-center justify-between gap-2 border-t border-line/10 pt-4">
              <Button
                variant="ghost"
                disabled={safeStep === 0}
                iconLeft={<ChevronLeft className="h-4 w-4" />}
                onClick={() => {
                  setLastCheck(null);
                  setStep((s) => Math.max(0, s - 1));
                }}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {tryAgain && singleQuestion && !isLocked ? (
                  <Button
                    variant="primary"
                    disabled={!isAnswered(page, answers[page.id])}
                    onClick={checkCurrent}
                  >
                    Submit answer
                  </Button>
                ) : null}
                {safeStep < pages.length - 1 ? (
                  <Button
                    variant="secondary"
                    disabled={nextBlocked}
                    iconRight={<ChevronRight className="h-4 w-4" />}
                    onClick={() => {
                      setLastCheck(null);
                      setStep((s) => Math.min(pages.length - 1, s + 1));
                    }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="success"
                    disabled={!allDone}
                    onClick={() => setResult(gradePreview(schema, answers, passMark, serve))}
                  >
                    Finish
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}
